/**
 * BOGOS auto-add first gift (cart drawer only).
 *
 * BOGOS has no native "default gift variant" option, so whenever its gift slider shows an offer
 * that still needs a pick, we click the first enabled "Add to cart" button of that offer (BOGOS's
 * own add flow, so properties/discount are handled by the app). This works per offer, so it also
 * covers carts where some gifts were already added natively and one offer is still pending, and
 * carts where several offers are pending at once (they are picked one after another).
 *
 * Offers that choose between several different products (not just shades) are manual: they are
 * always shown and never auto-clicked.
 *
 * Fails safe: if nothing matches or the click does nothing, that offer is revealed so the
 * shopper can pick manually.
 *
 * Opt-out (per offer): clicking "Change" or remove on a gift row reveals the offer that
 * reappears in the slider and stops auto-adding it. Opt-outs clear once nothing is pending and
 * no gift rows exist for a stable period (the offers stopped qualifying).
 *
 * While an auto-add is pending, a temporary skeleton gift row (shimmer image/title, "Free Gift"
 * label) is shown in the cart list. It stays a skeleton and is purely visual: it is removed when
 * nothing is left to auto-add (the drawer re-render also replaces it with the real gift row).
 *
 * Slider visibility (CSS only; BOGOS still renders into it): hidden by default, offers that are
 * opted-out/failed get `.bogos-offer-reveal` and the mount gets `.bogos-has-reveal`.
 */
(() => {
  // On the cart page the script tag carries data-cart-page and the same logic runs against the page's
  // cart items component instead of the drawer. Everywhere else this stays the drawer.
  const IS_CART_PAGE = !!document.currentScript?.hasAttribute('data-cart-page');
  let lastGliderNudge = 0;
  let gliderNudges = 0; // consecutive nudges without the track getting a width
  const MAX_GLIDER_NUDGES = 5; // hard cap so a stuck carousel can never cause a resize loop
  const DRAWER = IS_CART_PAGE ? 'cart-items-component:not([data-drawer])' : 'cart-drawer-component';
  const MOUNT = '.bogos-gift-slider-view';
  const ADD_BTN = '.bogos-gift-item-add-btn';
  const OFFER = '.bogos-slider-offer';
  const GIFT_ROW = '[data-bogos-gift="true"]';
  const OPT_OUT_KEY = 'asteri-bogos-auto-gift-opt-out-offers';
  const QUIET_MS = 1200; // BOGOS must be idle (no cart/gift requests, no bogos:gifts) this long first
  const SETTLE_MS = 400; // wait for BOGOS to finish rendering before clicking
  const RESET_MS = 2500; // "nothing pending, no gifts" must hold this long before opt-outs clear
  const RETRY_MS = 4000; // don't re-click the same offer more often than this
  const AWAIT_MS = 6000; // how long after Change/remove we wait for the offer to reappear

  /** @type {Map<string, number>} offer id -> last auto-click time */
  const lastClick = new Map();
  /** @type {Set<string>} offers whose auto-add did not work (memory only) */
  const failed = new Set();
  /** @type {{ snapshot: Set<string>, at: number } | null} set when Change/remove was clicked */
  let awaiting = null;
  let settleTimer = 0;
  let resetTimer = 0;
  let staleTimer = 0;
  let refreshedForCart = false; // one "re-render the slider" nudge per shopper cart change
  let refreshing = false;
  /** @type {Map<string, number>} offer id -> verify timer */
  const verifyTimers = new Map();
  /** @type {Map<string, number>} offer id -> retry attempts after a failed auto-add */
  const attempts = new Map();

  const root = document.documentElement;

  // TEMP DEBUG: set to false (or delete the debug lines) once the flow is verified.
  const DEBUG = false;
  const t0 = Date.now();
  const log = (...args) => {
    if (DEBUG) console.log(`[asteri-auto-gift +${((Date.now() - t0) / 1000).toFixed(2)}s]`, ...args);
  };
  const snapshotState = () => ({
    offers: Array.from(document.querySelectorAll(`${DRAWER} ${MOUNT} ${OFFER}`)).map((o) => ({
      id: o.getAttribute('data-offer'),
      buttons: o.querySelectorAll(ADD_BTN).length,
      reveal: o.classList.contains('bogos-offer-reveal'),
    })),
    giftRows: document.querySelectorAll(`${DRAWER} ${GIFT_ROW}`).length,
    optOuts: (() => {
      try {
        return sessionStorage.getItem('asteri-bogos-auto-gift-opt-out-offers');
      } catch {
        return 'n/a';
      }
    })(),
    failed: [...failed],
    awaiting: awaiting ? [...awaiting.snapshot] : null,
    busy: root.classList.contains('bogos-gift-changing'),
  });

  // BOGOS runs its own gift-processing cycle after every cart change and removes any gift that was
  // added while that cycle was in flight. So we only click once BOGOS has been quiet for a while.
  let bogosInflight = 0;
  let bogosLastActivity = 0;
  const isBogosCycleUrl = (u) => /sca_|secomapp_freegifts|\/cart\/(add|update|change|clear)/.test(String(u));
  const touchBogos = () => {
    bogosLastActivity = Date.now();
  };
  const quietRemaining = () =>
    bogosInflight > 0 ? 300 : Math.max(0, QUIET_MS - (Date.now() - bogosLastActivity));

  const trackedFetch = window.fetch;
  window.fetch = function (input) {
    const url = typeof input === 'string' ? input : input?.url;
    // A shopper/theme cart change (not BOGOS's own sca_ requests) re-arms the one-time slider refresh.
    if (/\/cart\/(add|change|update|clear)/.test(String(url)) && !/sca_/.test(String(url))) refreshedForCart = false;
    if (!isBogosCycleUrl(url)) return trackedFetch.apply(this, arguments);
    bogosInflight++;
    touchBogos();
    const done = () => {
      bogosInflight = Math.max(0, bogosInflight - 1);
      touchBogos();
    };
    const request = trackedFetch.apply(this, arguments);
    request.then(done, done);
    return request;
  };
  document.addEventListener('bogos:gifts', touchBogos, true);

  if (DEBUG) {
    // Log every cart request with its body and caller, to see who adds/removes gift lines.
    const isCartUrl = (u) => /\/cart\/(add|change|update|clear)/.test(String(u));
    const callerOf = () => (new Error().stack || '').split('\n').slice(3, 7).map((l) => l.trim()).join(' | ');

    const origFetch = window.fetch;
    window.fetch = function (input, init) {
      const url = typeof input === 'string' ? input : input?.url;
      if (isCartUrl(url)) log('FETCH', url, init?.body ? String(init.body).slice(0, 400) : '', '| caller:', callerOf());
      return origFetch.apply(this, arguments);
    };

    const origOpen = XMLHttpRequest.prototype.open;
    const origSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__asteriUrl = url;
      return origOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function (body) {
      if (isCartUrl(this.__asteriUrl)) log('XHR', this.__asteriUrl, body ? String(body).slice(0, 400) : '', '| caller:', callerOf());
      return origSend.apply(this, arguments);
    };

    // Network requests seen by the browser itself (catches fetches that bypass the wrappers above).
    try {
      new PerformanceObserver((list) => {
        list.getEntries().forEach((e) => {
          if (/\/cart\/|bogos|freegifts|giftbox/i.test(e.name)) log('NET', e.initiatorType, e.name.slice(0, 200));
        });
      }).observe({ type: 'resource', buffered: false });
    } catch {
      // PerformanceObserver unsupported: skip.
    }

    // Gift rows appearing/disappearing anywhere in the DOM.
    new MutationObserver((records) => {
      for (const r of records) {
        r.addedNodes.forEach((n) => {
          if (n instanceof Element && (n.matches(GIFT_ROW) || n.querySelector(GIFT_ROW))) log('DOM: gift row ADDED');
        });
        r.removedNodes.forEach((n) => {
          if (n instanceof Element && (n.matches(GIFT_ROW) || n.querySelector(GIFT_ROW))) log('DOM: gift row REMOVED');
        });
      }
    }).observe(document.documentElement, { childList: true, subtree: true });

    // Cart contents over time, independent of who changes it.
    let lastSig = '';
    setInterval(async () => {
      try {
        const cart = await (await origFetch.call(window, `${window.Shopify?.routes?.root || '/'}cart.js`)).json();
        const sig = cart.items.map((i) => `${i.variant_id}x${i.quantity}${i.properties && Object.keys(i.properties).length ? ' ' + JSON.stringify(i.properties) : ''}`).join(' | ');
        if (sig !== lastSig) {
          lastSig = sig;
          log('CART', cart.item_count, 'items:', sig);
        }
      } catch {
        // ignore
      }
    }, 1000);

    document.addEventListener('bogos:gifts', (e) => log('event bogos:gifts', 'items:', e.detail?.items?.length), true);
  }

  // Hide only while this script is active; if it never loads, the slider stays visible.
  const style = document.createElement('style');
  style.textContent = `
    html.bogos-auto-gift-on:not(.bogos-gift-picker-open) ${DRAWER} ${MOUNT}:not(.bogos-has-reveal){display:none !important;}
    html.bogos-auto-gift-on:not(.bogos-gift-picker-open) ${DRAWER} ${MOUNT} ${OFFER}:not(.bogos-offer-reveal){display:none !important;}
    .bogos-auto-gift-placeholder .bogos-ph-img{width:100%;aspect-ratio:1;background:rgb(0 0 0 / 8%);border-radius:4px;overflow:hidden;}
    .bogos-auto-gift-placeholder .bogos-ph-line{display:block;height:12px;margin:6px 0;border-radius:4px;background:rgb(0 0 0 / 8%);}
    .bogos-auto-gift-placeholder .bogos-ph-line--title{width:70%;}
    .bogos-auto-gift-placeholder .bogos-ph-line--detail{width:40%;}
    .bogos-auto-gift-placeholder .is-skeleton{position:relative;overflow:hidden;}
    .bogos-auto-gift-placeholder .is-skeleton::after{content:'';position:absolute;inset:0;transform:translateX(-100%);
      background:linear-gradient(90deg,transparent,rgb(255 255 255 / 60%),transparent);animation:bogos-ph-shimmer 1.25s infinite;}
    @keyframes bogos-ph-shimmer{to{transform:translateX(100%);}}
    @media (prefers-reduced-motion:reduce){.bogos-auto-gift-placeholder .is-skeleton::after{animation:none;}}`;
  document.head.appendChild(style);
  root.classList.add('bogos-auto-gift-on');

  /** @returns {Element | null} */
  const getDrawer = () => document.querySelector(DRAWER);

  const getOffers = () => Array.from(getDrawer()?.querySelectorAll(`${MOUNT} ${OFFER}`) || []);
  const offerId = (offer) => offer.getAttribute('data-offer') || '';

  const readOptOuts = () => {
    try {
      const parsed = JSON.parse(sessionStorage.getItem(OPT_OUT_KEY) || '[]');
      return new Set(Array.isArray(parsed) ? parsed.map(String) : []);
    } catch {
      return new Set();
    }
  };

  const writeOptOuts = (set) => {
    try {
      if (set.size) sessionStorage.setItem(OPT_OUT_KEY, JSON.stringify([...set]));
      else sessionStorage.removeItem(OPT_OUT_KEY);
    } catch {
      // sessionStorage unavailable: auto-add still works, opt-outs just aren't remembered.
    }
  };

  /**
   * An offer whose add buttons point at more than one distinct product (e.g. a pick between two
   * different sachets) is a manual choice: the shopper picks, we never auto-click it.
   * Offers with a single product (any number of shades) are auto-picked.
   */
  const isManualOffer = (offer) =>
    new Set(Array.from(offer.querySelectorAll(ADD_BTN)).map((b) => b.getAttribute('data-product'))).size > 1;

  const isRevealed = (id, optOuts) => optOuts.has(id) || failed.has(id);

  const isBusy = () =>
    root.classList.contains('bogos-gift-changing') ||
    document.querySelector(`${DRAWER} ${MOUNT}[aria-busy="true"]`) !== null;

  const PLACEHOLDER = '.bogos-auto-gift-placeholder';

  const removePlaceholder = () => {
    document.querySelectorAll(PLACEHOLDER).forEach((el) => el.remove());
  };

  /** Adds the skeleton gift row (same row/cell classes as a real gift row) to the drawer cart list. */
  const showPlaceholder = (drawer) => {
    if (drawer.querySelector(PLACEHOLDER)) return;
    const tbody = drawer.querySelector('.cart-items__table tbody');
    if (!tbody) return;

    const row = document.createElement('tr');
    row.className = 'cart-items__table-row cart-item-card cart-items__table-row--bogos-gift bogos-auto-gift-placeholder';
    row.setAttribute('role', 'row');
    row.setAttribute('aria-busy', 'true');
    row.innerHTML = `
      <td class="cart-items__media" role="cell"><div class="cart-items__media-container cart-items__media-container--static"><div class="bogos-ph-img is-skeleton"></div></div></td>
      <td class="cart-items__details cart-primary-typography" role="cell">
        <div class="cart-items__info-row"><div class="cart-items__product-info">
          <span class="bogos-ph-line bogos-ph-line--title is-skeleton"></span>
        </div></div>
        <div class="cart-items__variants-wrapper"><span class="bogos-ph-line bogos-ph-line--detail is-skeleton"></span></div>
      </td>
      <td class="cart-items__quantity" role="cell"><div class="cart-items__quantity-row"><div class="cart-items__line-price cart-secondary-typography"><span class="cart-items__free-gift">Free Gift</span></div></div></td>
      <td class="cart-items__price cart-secondary-typography" role="cell"></td>`;
    tbody.appendChild(row);
  };

  /** Offers that still need a pick and are allowed to be auto-picked right now. */
  const getEligibleOffers = (optOuts) =>
    getOffers().filter(
      (offer) => offer.querySelector(ADD_BTN) && !isManualOffer(offer) && !isRevealed(offerId(offer), optOuts)
    );

  /**
   * Resolves a pending Change/remove (offers that appear afterwards get opted out), then applies
   * the reveal classes. Runs on every drawer mutation, before anything is clicked.
   */
  const sync = () => {
    const drawer = getDrawer();
    if (!drawer) return;

    const offers = getOffers();
    let optOuts = readOptOuts();

    if (awaiting) {
      const fresh = offers.map(offerId).filter((id) => id && !awaiting.snapshot.has(id));
      if (fresh.length) {
        fresh.forEach((id) => optOuts.add(id));
        writeOptOuts(optOuts);
        log('opt-out: offers reappeared after Change/remove', fresh);
        awaiting = null;
      } else if (Date.now() - awaiting.at > AWAIT_MS) {
        log('awaiting timed out, no new offer appeared');
        awaiting = null;
      }
    }

    // While Change/remove is in flight, show the slider (and its loader) right away.
    root.classList.toggle('bogos-gift-picker-open', awaiting !== null);

    let anyReveal = false;
    for (const offer of offers) {
      const reveal = isRevealed(offerId(offer), optOuts) || isManualOffer(offer);
      offer.classList.toggle('bogos-offer-reveal', reveal);
      anyReveal = anyReveal || reveal;
    }
    drawer.querySelector(MOUNT)?.classList.toggle('bogos-has-reveal', anyReveal);

    // Cart page only: BOGOS sizes its glider carousel while the mount can be hidden/loading (0 width).
    // If the carousel is on screen but its track is still 0 wide, nudge it to re-measure (it listens
    // to window resize). Throttled; stops by itself once the track has a width.
    if (IS_CART_PAGE) {
      const glider = drawer.querySelector(`${MOUNT} .glider`);
      const track = glider?.querySelector('.glider-track');
      if (track && track.offsetWidth > 0) gliderNudges = 0; // healthy again: allow future nudges
      if (
        glider && track && glider.clientWidth > 0 && track.offsetWidth === 0 &&
        gliderNudges < MAX_GLIDER_NUDGES && Date.now() - lastGliderNudge > 300
      ) {
        gliderNudges++;
        lastGliderNudge = Date.now();
        requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
      }
    }
  };

  /**
   * The cart section re-render wipes BOGOS's slider, and BOGOS only redraws it on a `bogos:gifts` event.
   * If BOGOS then removes a gift without redrawing, the slider (e.g. the sachet picker) stays empty.
   * When the slider is empty and BOGOS is idle, ask it once to redraw (same event the theme already
   * uses for discount updates). Once per shopper cart change, so it can't loop.
   */
  const refreshEmptySlider = async () => {
    if (refreshedForCart || refreshing || !getDrawer()?.querySelector(MOUNT)) return;
    const wait = quietRemaining();
    if (wait > 0) {
      clearTimeout(settleTimer);
      settleTimer = window.setTimeout(tryAutoAdd, wait + 50);
      return;
    }
    refreshing = true;
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
      if (!response.ok) return;
      const cart = await response.json();
      if (!cart?.item_count) return;
      refreshedForCart = true;
      log('slider empty while BOGOS idle: asking BOGOS to re-render');
      document.dispatchEvent(new CustomEvent('bogos:gifts', { detail: cart }));
    } catch {
      // Silently ignore BOGOS refresh failures.
    } finally {
      refreshing = false;
    }
  };

  const tryAutoAdd = () => {
    const drawer = getDrawer();
    if (!drawer) return;

    sync();
    const optOuts = readOptOuts();
    const offers = getOffers();
    const hasGiftRow = drawer.querySelector(GIFT_ROW) !== null;

    // Nothing pending and no gifts: the offers stopped qualifying, so clear opt-outs once stable.
    if (!hasGiftRow && offers.length === 0) {
      removePlaceholder();
      failed.clear();
      if (!resetTimer && optOuts.size) {
        log('opt-out reset: timer STARTED', [...optOuts]);
        resetTimer = window.setTimeout(() => {
          resetTimer = 0;
          const d = getDrawer();
          if (d && !d.querySelector(GIFT_ROW) && !d.querySelector(`${MOUNT} ${OFFER}`) && !isBusy()) {
            log('opt-out reset: CLEARED');
            writeOptOuts(new Set());
            sync();
          } else {
            log('opt-out reset: timer fired but NOT cleared', {
              giftRow: !!d?.querySelector(GIFT_ROW),
              offer: !!d?.querySelector(`${MOUNT} ${OFFER}`),
              busy: isBusy(),
            });
          }
        }, RESET_MS);
      }
      refreshEmptySlider();
      return;
    }

    if (resetTimer) {
      log('opt-out reset: timer CANCELLED (offers/gifts reappeared)', snapshotState());
      clearTimeout(resetTimer);
      resetTimer = 0;
    }

    // Other offers (e.g. a manual sachet offer) can keep the slider non-empty, so the reset above never
    // runs. Per offer: an opted-out offer that has left the slider (stopped qualifying) is cleared after
    // a stable period. Re-checked when the timer fires, so a brief BOGOS re-render can't clear it.
    const present = new Set(offers.map(offerId));
    if (!awaiting && !staleTimer && [...optOuts].some((id) => !present.has(id))) {
      staleTimer = window.setTimeout(() => {
        staleTimer = 0;
        const now = new Set(getOffers().map(offerId));
        const current = readOptOuts();
        const gone = [...current].filter((id) => !now.has(id));
        if (gone.length && !awaiting && !isBusy()) {
          gone.forEach((id) => current.delete(id));
          writeOptOuts(current);
          log('opt-out reset (per offer): CLEARED', gone);
          sync();
        }
      }, RESET_MS);
    }

    const eligible = getEligibleOffers(optOuts);
    const wait = eligible.length ? quietRemaining() : 0;
    if (wait > 0) {
      log('waiting for BOGOS to settle', wait, 'ms');
      clearTimeout(settleTimer);
      settleTimer = window.setTimeout(tryAutoAdd, wait + 50);
      return;
    }
    log('tryAutoAdd', { eligible: eligible.map(offerId), busy: isBusy(), awaiting: !!awaiting, state: snapshotState() });
    if (!eligible.length) removePlaceholder();
    if (!eligible.length || isBusy() || awaiting) return;

    const now = Date.now();
    for (const offer of eligible) {
      const id = offerId(offer);
      if (now - (lastClick.get(id) || 0) < RETRY_MS) {
        log('skip offer (clicked recently)', id);
        continue;
      }

      const button = Array.from(offer.querySelectorAll(ADD_BTN)).find(
        (b) => b instanceof HTMLButtonElement && !b.disabled && b.getAttribute('aria-disabled') !== 'true'
      );
      if (!button) {
        failed.add(id); // nothing clickable: let the shopper pick manually
        log('FAILED: no enabled button for offer', id);
        continue;
      }

      lastClick.set(id, now);
      log('CLICK auto-add', { offer: id, variant: button.getAttribute('data-variant'), product: button.getAttribute('data-product') });
      button.click();

      // If this offer is still asking for a pick shortly after, reveal it instead of hiding it.
      clearTimeout(verifyTimers.get(id));
      verifyTimers.set(
        id,
        window.setTimeout(() => {
          const stillThere = getOffers().some((o) => offerId(o) === id && o.querySelector(ADD_BTN));
          log('verify after click', id, stillThere ? 'offer STILL shown (retry once, then reveal)' : 'offer gone (ok)', snapshotState());
          if (!stillThere) attempts.delete(id);
          if (stillThere && !attempts.get(id)) {
            // BOGOS may have removed the gift mid-cycle: try once more after it settles.
            attempts.set(id, 1);
            lastClick.delete(id);
            log('retrying offer once', id);
            schedule();
          } else if (stillThere) {
            attempts.delete(id);
            failed.add(id);
            sync();
            if (!getEligibleOffers(readOptOuts()).length) removePlaceholder();
          }
        }, RETRY_MS)
      );
      // One offer per pass; the cart update re-renders the drawer and re-triggers this check.
      break;
    }
    sync();
  };

  const schedule = () => {
    sync();

    // An auto-add is about to happen: show the skeleton right away.
    const drawer = getDrawer();
    if (drawer && !isBusy() && !awaiting && getEligibleOffers(readOptOuts()).length) {
      if (!drawer.querySelector(PLACEHOLDER)) log('show placeholder');
      showPlaceholder(drawer);
    }
    clearTimeout(settleTimer);
    settleTimer = window.setTimeout(tryAutoAdd, SETTLE_MS);
  };

  // Shopper intent: Change / remove on a gift row means "don't auto-add the offer that reappears".
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const control = target.closest('.cart-items__change, .cart-items__remove');
      if (!control || !control.closest(`${DRAWER} ${GIFT_ROW}`)) return;
      awaiting = { snapshot: new Set(getOffers().map(offerId)), at: Date.now() };
      log('shopper clicked', control.className, 'snapshot:', [...awaiting.snapshot]);
      removePlaceholder();
      sync();
    },
    true
  );

  let observedDrawer = null;
  const drawerObserver = new MutationObserver(schedule);

  const attach = () => {
    const drawer = getDrawer();
    if (!drawer || drawer === observedDrawer) return false;
    if (observedDrawer) log('DRAWER ELEMENT WAS REPLACED, re-attaching observer');
    drawerObserver.disconnect();
    drawerObserver.observe(drawer, { childList: true, subtree: true });
    observedDrawer = drawer;
    return true;
  };

  const start = () => {
    if (!attach()) return false;
    log('started', snapshotState());
    // The drawer element itself can be swapped by a section re-render; keep following it.
    new MutationObserver(() => {
      if (attach()) schedule();
    }).observe(document.body, { childList: true, subtree: true });
    schedule();
    return true;
  };

  if (!start()) {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  }
})();
